import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Shield, BarChart3, Target, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";

const STORAGE_KEY = "cycy.cookie-consent.v1";
const CONSENT_ID_KEY = "cycy.cookie-consent.id";
const POLICY_VERSION = 1;
// Re-Consent nach 12 Monaten
const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000;

export const COOKIE_CONSENT_EVENT = "cycy:open-cookie-settings";
export const COOKIE_CONSENT_UPDATED_EVENT = "cycy:cookie-consent-updated";

export type ConsentCategories = {
  essential: true; // immer aktiv
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
};

type StoredConsent = {
  categories: ConsentCategories;
  decidedAt: string; // ISO
  version: typeof POLICY_VERSION;
};

const DEFAULT_CATEGORIES: ConsentCategories = {
  essential: true,
  functional: false,
  analytics: false,
  marketing: false,
};

function readConsent(): StoredConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (parsed?.version !== POLICY_VERSION) return null;
    // TTL prüfen — nach 12 Monaten erneut nachfragen
    const decidedAt = new Date(parsed.decidedAt).getTime();
    if (!Number.isFinite(decidedAt)) return null;
    if (Date.now() - decidedAt > CONSENT_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function getOrCreateConsentId(): string {
  try {
    const existing = localStorage.getItem(CONSENT_ID_KEY);
    if (existing) return existing;
    const id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `cid_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(CONSENT_ID_KEY, id);
    return id;
  } catch {
    return `cid_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }
}

async function logConsentToServer(categories: ConsentCategories) {
  try {
    const consent_id = getOrCreateConsentId();
    await supabase.from("consent_log").insert([
      {
        consent_id,
        categories: categories as unknown as never,
        user_agent:
          typeof navigator !== "undefined"
            ? navigator.userAgent.slice(0, 500)
            : null,
        policy_version: POLICY_VERSION,
      },
    ]);
  } catch {
    // Logging darf den Nutzer-Flow nicht blockieren
  }
}

function writeConsent(categories: ConsentCategories) {
  const payload: StoredConsent = {
    categories,
    decidedAt: new Date().toISOString(),
    version: POLICY_VERSION,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  window.dispatchEvent(
    new CustomEvent(COOKIE_CONSENT_UPDATED_EVENT, { detail: payload })
  );
  // Server-seitiger Nachweis (DSGVO Art. 7 Abs. 1) — Fire-and-forget
  void logConsentToServer(categories);
}

export function getConsent(): ConsentCategories {
  return readConsent()?.categories ?? DEFAULT_CATEGORIES;
}

/** Hilfs-Hook: prüft, ob eine Kategorie aktuell eingewilligt ist (reaktiv). */
export function hasConsent(category: keyof ConsentCategories): boolean {
  return Boolean(getConsent()[category]);
}

export function openCookieSettings() {
  window.dispatchEvent(new Event(COOKIE_CONSENT_EVENT));
}

/** Liefert true, wenn der Browser ein Global-Privacy-Control-Signal sendet. */
function hasGpcSignal(): boolean {
  try {
    return Boolean(
      (navigator as Navigator & { globalPrivacyControl?: boolean })
        .globalPrivacyControl
    );
  } catch {
    return false;
  }
}

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [categories, setCategories] =
    useState<ConsentCategories>(DEFAULT_CATEGORIES);

  useEffect(() => {
    const stored = readConsent();
    if (!stored) {
      // Wenn der Browser GPC-Signal sendet, automatisch nur essenzielle setzen
      // (kein zusätzliches Banner-Pushing) – Nutzer kann jederzeit ändern.
      if (hasGpcSignal()) {
        writeConsent(DEFAULT_CATEGORIES);
        setCategories(DEFAULT_CATEGORIES);
        return;
      }
      const t = setTimeout(() => setShowBanner(true), 400);
      return () => clearTimeout(t);
    }
    setCategories(stored.categories);
  }, []);

  useEffect(() => {
    const open = () => {
      const stored = readConsent();
      if (stored) setCategories(stored.categories);
      setShowSettings(true);
    };
    window.addEventListener(COOKIE_CONSENT_EVENT, open);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, open);
  }, []);

  const acceptAll = () => {
    const all: ConsentCategories = {
      essential: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    writeConsent(all);
    setCategories(all);
    setShowBanner(false);
    setShowSettings(false);
  };

  const rejectAll = () => {
    writeConsent(DEFAULT_CATEGORIES);
    setCategories(DEFAULT_CATEGORIES);
    setShowBanner(false);
    setShowSettings(false);
  };

  const saveSelection = () => {
    writeConsent({ ...categories, essential: true });
    setShowBanner(false);
    setShowSettings(false);
  };

  const SettingsBody = (
    <div className="space-y-1.5 sm:space-y-4">
      <CategoryRow
        icon={<Shield className="h-4 w-4" />}
        title="Notwendig"
        description="Erforderlich für den Betrieb des Shops (Warenkorb, Sitzung, Sicherheit, Cookie-Einstellungen). Diese Cookies können nicht deaktiviert werden."
        checked
        disabled
      />
      <CategoryRow
        icon={<Settings2 className="h-4 w-4" />}
        title="Funktional"
        description="Verbessern Komfort und Personalisierung (z. B. zuletzt angesehene Produkte). Aktuell nicht aktiv im Einsatz."
        checked={categories.functional}
        onChange={(v) => setCategories((c) => ({ ...c, functional: v }))}
      />
      <CategoryRow
        icon={<BarChart3 className="h-4 w-4" />}
        title="Analyse / Statistik"
        description="Helfen uns zu verstehen, wie unser Shop genutzt wird (anonymisierte Reichweitenmessung). Aktuell nicht aktiv im Einsatz."
        checked={categories.analytics}
        onChange={(v) => setCategories((c) => ({ ...c, analytics: v }))}
      />
      <CategoryRow
        icon={<Target className="h-4 w-4" />}
        title="Marketing"
        description="Werden für interessenbezogene Werbung genutzt. Aktuell nicht aktiv im Einsatz."
        checked={categories.marketing}
        onChange={(v) => setCategories((c) => ({ ...c, marketing: v }))}
      />
      <p className="hidden sm:block text-[11px] sm:text-xs text-muted-foreground leading-snug">
        Weitere Informationen finden Sie in unserer{" "}
        <Link to="/datenschutz" className="underline hover:text-primary">
          Datenschutzerklärung
        </Link>
        . Sie können Ihre Auswahl jederzeit über den Link{" "}
        <em>„Cookie-Einstellungen"</em> im Footer ändern. Ihre Einwilligung
        bleibt 12 Monate gültig.
      </p>
      <p className="sm:hidden text-[10px] text-muted-foreground leading-tight">
        Tippen Sie auf eine Kategorie für Details. Mehr in der{" "}
        <Link to="/datenschutz" className="underline hover:text-primary">
          Datenschutzerklärung
        </Link>
        .
      </p>
    </div>
  );

  return (
    <>
      {/* Banner */}
      {showBanner && !showSettings && (
        <div
          role="dialog"
          aria-live="polite"
          aria-label="Cookie-Einstellungen"
          className="fixed inset-x-0 bottom-0 z-[60] p-1.5 sm:p-3 animate-in slide-in-from-bottom duration-300"
        >
          <div className="mx-auto max-w-2xl rounded-lg sm:rounded-xl border border-border bg-background/95 backdrop-blur-md shadow-xl p-2.5 sm:p-4">
            <div className="flex items-start gap-2">
              <div className="hidden sm:flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Cookie className="h-3.5 w-3.5" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-heading text-[13px] sm:text-base font-semibold mb-0.5 leading-tight">
                  Wir respektieren Ihre Privatsphäre
                </h2>
                <p className="text-[11px] sm:text-xs text-muted-foreground leading-snug">
                  Technisch notwendige Cookies sind aktiv. Optionale Cookies nur
                  mit Ihrer Einwilligung.{" "}
                  <Link to="/datenschutz" className="underline hover:text-primary">
                    Datenschutz
                  </Link>
                  .
                </p>
              </div>
            </div>
            <div className="mt-2.5 sm:mt-3 grid grid-cols-2 sm:flex sm:flex-row sm:justify-end gap-1.5 sm:gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowSettings(true)}
                className="h-8 px-2.5 text-xs sm:h-9 sm:px-3 sm:text-sm sm:order-1"
              >
                Einstellungen
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={rejectAll}
                className="h-8 px-2.5 text-xs sm:h-9 sm:px-3 sm:text-sm sm:order-2"
              >
                Nur notwendige
              </Button>
              <Button
                size="sm"
                onClick={acceptAll}
                className="col-span-2 h-8 px-2.5 text-xs sm:h-9 sm:px-3 sm:text-sm sm:order-3 sm:col-span-1"
              >
                Alle akzeptieren
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-lg w-[calc(100vw-1rem)] sm:w-full p-3 sm:p-6 max-h-[92dvh] overflow-y-auto rounded-xl gap-2 sm:gap-4">
          <DialogHeader className="space-y-0.5 sm:space-y-1.5 text-left">
            <DialogTitle className="flex items-center gap-2 text-sm sm:text-lg">
              <Cookie className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              Cookie-Einstellungen
            </DialogTitle>
            <DialogDescription className="hidden sm:block text-xs sm:text-sm leading-snug">
              Wählen Sie, welche Kategorien Sie zulassen möchten. Ihre Auswahl
              wird lokal in Ihrem Browser gespeichert.
            </DialogDescription>
          </DialogHeader>
          {SettingsBody}
          <div className="mt-1 grid grid-cols-2 sm:flex sm:flex-row sm:justify-end gap-1.5 sm:gap-2">
            <Button variant="ghost" size="sm" onClick={rejectAll} className="h-8 text-[11px] px-2 sm:text-sm sm:h-10 sm:px-4">
              Nur notwendige
            </Button>
            <Button variant="outline" size="sm" onClick={saveSelection} className="h-8 text-[11px] px-2 sm:text-sm sm:h-10 sm:px-4">
              Auswahl speichern
            </Button>
            <Button size="sm" onClick={acceptAll} className="col-span-2 h-8 text-[11px] px-2 sm:text-sm sm:h-10 sm:px-4">
              Alle akzeptieren
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const CategoryRow = ({
  icon,
  title,
  description,
  checked,
  onChange,
  disabled,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  checked: boolean;
  onChange?: (v: boolean) => void;
  disabled?: boolean;
}) => (
  <div className="flex items-start gap-2 sm:gap-3 rounded-lg sm:rounded-xl border border-border bg-card/50 p-2 sm:p-3">
    <div className="hidden sm:flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
      {icon}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between gap-2 sm:gap-3">
        <p className="font-medium text-[13px] sm:text-sm leading-tight">{title}</p>
        <Switch
          checked={checked}
          disabled={disabled}
          onCheckedChange={onChange}
          aria-label={`${title} ${checked ? "deaktivieren" : "aktivieren"}`}
        />
      </div>
      <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1 leading-tight sm:leading-snug line-clamp-2 sm:line-clamp-none">
        {description}
      </p>
    </div>
  </div>
);

export default CookieConsent;
